import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, RotateCcw, AlertTriangle, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MediaRestorePanel = ({ onDone }) => {
  const { token } = useAuth();
  const [sourceUrl, setSourceUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const poll = async () => {
    const { data } = await axios.get(`${API}/admin/media/restore-missing`, { headers });
    if (data.status === 'running') {
      setProgress(data.total ? `${data.processed || 0}/${data.total}` : '');
      return setTimeout(() => poll().catch(fail), 3000);
    }
    setLoading(false);
    if (data.status === 'error') return setError(data.error || 'Gjenoppretting feilet');
    setResult(data.result);
    onDone?.();
  };

  const fail = (e) => {
    setLoading(false);
    setError(e.response?.data?.detail || 'Gjenoppretting feilet');
  };

  const run = async () => {
    setLoading(true); setError(''); setResult(null); setProgress('');
    try {
      await axios.post(`${API}/admin/media/restore-missing`, { source_url: sourceUrl }, { headers });
      await poll();
    } catch (e) {
      fail(e);
    }
  };

  return (
    <Card className="bg-gray-900/80 border-amber-700/50" data-testid="media-restore-panel">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2 text-white font-medium">
          <RotateCcw className="w-4 h-4 text-amber-400" /> Gjenopprett manglende bilder
        </div>
        <p className="text-sm text-gray-400">
          Finner bilder som brukes på nettsiden men mangler i dette miljøet, og kopierer dem permanent fra kilde-miljøet (f.eks. preview-adressen).
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} placeholder="https://kilde-miljø.no"
            className="bg-gray-800 border-gray-600 text-white" data-testid="media-restore-source-input" />
          <Button onClick={run} disabled={loading || !sourceUrl} className="bg-amber-600 hover:bg-amber-700" data-testid="media-restore-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RotateCcw className="w-4 h-4 mr-2" />}
            {loading ? `Gjenoppretter... ${progress}` : 'Gjenopprett'}
          </Button>
        </div>
        {error && <p className="text-sm text-red-400" data-testid="media-restore-error">{error}</p>}
        {result && (
          <div className="text-sm space-y-2" data-testid="media-restore-result">
            <p className="text-green-400 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              {result.checked} sjekket · {result.already_ok} OK · {result.restored.length} gjenopprettet · {result.missing.length} mangler
            </p>
            {result.missing.length > 0 && (
              <div className="bg-red-950/40 border border-red-800/50 rounded p-2" data-testid="media-restore-missing-list">
                <p className="text-red-300 flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4" /> Finnes ikke noe sted – må lastes opp på nytt:
                </p>
                <ul className="max-h-40 overflow-y-auto text-gray-300 font-mono text-xs">
                  {result.missing.map((f) => <li key={f}>{f}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
